import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Avatar,
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { Teams } from '@par/shared/types/game';
import { DbLeagueTeam } from '@par/shared/types/league';
import { fetchGet, fetchPost } from '@par/shared/api/common';
import { uniq } from 'lodash';
import { WeekData } from '../components/league';

type PropTypes = {
  close: () => void;
  open: boolean;
  profileId: string;
  teams: DbLeagueTeam[];
};

export const LeagueAddMatchScheduleDialog = ({
  close,
  open,
  profileId,
  teams,
}: PropTypes) => {
  const theme = useTheme();
  const [schedule, setSchedule] = useState<Dayjs | null>();
  const [checkedHomeTeams, setCheckedHomeTeams] =
    useState<Record<string, boolean>>();
  const [checkedAwayTeams, setCheckedAwayTeams] =
    useState<Record<string, boolean>>();
  const [seasons, setSeasons] = useState<number[]>();
  const [weeks, setWeeks] = useState<WeekData[]>();
  const [season, setSeason] = useState<string>();
  const [week, setWeek] = useState<string>();
  const [numOfGames, setNumOfGames] = useState(2);

  useEffect(() => {
    if (!open) return;
    const checkedHomeTeams = teams.reduce(
      (a, c) => ((a[c._id] = false), a),
      {}
    );
    setCheckedHomeTeams(checkedHomeTeams);
    const checkedAwayTeams = teams.reduce(
      (a, c) => ((a[c._id] = false), a),
      {}
    );
    setCheckedAwayTeams(checkedAwayTeams);
    fetchWeeks();
  }, [open]);

  const handleCheckHomeTeam = (checked: boolean, teamId: string) => {
    const filtered = Object.values(checkedHomeTeams).filter(
      (checked) => checked
    );
    if (checked && filtered.length >= 1) return;
    setCheckedHomeTeams((prev) => ({
      ...prev,
      [teamId]: checked,
    }));
  };

  const handleCheckAwayTeam = (checked: boolean, teamId: string) => {
    const filtered = Object.values(checkedAwayTeams).filter(
      (checked) => checked
    );
    if (checked && filtered.length >= 1) return;
    setCheckedAwayTeams((prev) => ({
      ...prev,
      [teamId]: checked,
    }));
  };

  const handleAddScheduleClick = async () => {
    if (!schedule || season === undefined || week === undefined) return;
    const homeTeamId = Object.keys(checkedHomeTeams).filter(
      (key) => checkedHomeTeams[key] === true
    );
    const awayTeamId = Object.keys(checkedAwayTeams).filter(
      (key) => checkedAwayTeams[key] === true
    );
    const res = await fetchPost('/league/add-match-schedule', {
      profileId,
      schedule: schedule.valueOf(),
      homeTeamId: homeTeamId[0],
      awayTeamId: awayTeamId[0],
      season,
      week,
      numOfGames,
    });
    console.log('handleAddScheduleClick', res);
    close();
  };

  const fetchWeeks = async () => {
    const res = await fetchGet('/league/weeks/all');
    setWeeks(res.weeks);
    const seasons = uniq(res.weeks.map((w) => w.season) as number[]);
    setSeasons(seasons);
  };

  const handleSeasonSelect = (event: SelectChangeEvent) => {
    setSeason(event.target.value as string);
  };

  const handleWeekSelect = (event: SelectChangeEvent) => {
    setWeek(event.target.value as string);
  };

  const handleNumOfGamesChange = (event: SelectChangeEvent) => {
    setNumOfGames(+event.target.value);
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Add a match schedule</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={2}>
          <Stack direction={'row'} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Season</InputLabel>
              <Select
                value={season}
                label="Season"
                onChange={handleSeasonSelect}
              >
                {seasons?.map((s) => (
                  <MenuItem value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Week</InputLabel>
              <Select value={week} label="Week" onChange={handleWeekSelect}>
                {weeks?.map((w) => (
                  <MenuItem
                    value={w.week}
                  >{`Season ${w.season} | Week ${w.week}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="standard" sx={{ minWidth: 100 }}>
              <InputLabel>Num of games</InputLabel>
              <Select
                value={numOfGames.toString()}
                onChange={handleNumOfGamesChange}
              >
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              value={schedule}
              onChange={(newValue) => setSchedule(newValue)}
            />
          </LocalizationProvider>
          <Stack direction="row" spacing={2}>
            <Stack spacing={2} width="50%">
              <Typography variant="h6" fontWeight={600}>
                Home
              </Typography>
              {checkedHomeTeams ? (
                <FormControl component="fieldset" sx={{ paddingLeft: 1 }}>
                  <FormGroup>
                    {teams.map((t) => (
                      <FormControlLabel
                        value="top"
                        control={
                          <Checkbox
                            checked={checkedHomeTeams[t._id]}
                            onChange={(e) =>
                              handleCheckHomeTeam(e.target.checked, t._id)
                            }
                            size="small"
                          />
                        }
                        label={t.teamName}
                        labelPlacement="end"
                        sx={{
                          paddingLeft: 1,
                        }}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              ) : null}
            </Stack>
            <Stack spacing={2} width="50%">
              <Typography variant="h6" fontWeight={600}>
                Away
              </Typography>
              {checkedAwayTeams ? (
                <FormControl component="fieldset" sx={{ paddingLeft: 1 }}>
                  <FormGroup>
                    {teams.map((t) => (
                      <FormControlLabel
                        value="top"
                        control={
                          <Checkbox
                            checked={checkedAwayTeams[t._id]}
                            onChange={(e) =>
                              handleCheckAwayTeam(e.target.checked, t._id)
                            }
                            size="small"
                          />
                        }
                        label={t.teamName}
                        labelPlacement="end"
                        sx={{
                          paddingLeft: 1,
                        }}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} fullWidth>
          Cancel
        </Button>
        <Button onClick={handleAddScheduleClick} fullWidth>
          Add Match Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
};
